/**
 * StrategyList - Display and manage saved strategies
 */

import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Tag, 
  Button, 
  Space, 
  Empty, 
  Spin, 
  Typography, 
  Popconfirm,
  message,
  Modal,
  Statistic
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  ReloadOutlined,
  TrophyOutlined,
  RiseOutlined,
  FallOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';
import { Strategy } from './types';
import { getStrategies, deleteStrategy } from '../../services/strategyService';
import BacktestResultsComponent from './BacktestResults';

const { Title, Text } = Typography;

interface StrategyListProps {
  onCreateNew: () => void;
  onEdit: (strategy: Strategy) => void;
}

const StrategyList: React.FC<StrategyListProps> = ({ onCreateNew, onEdit }) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewingStrategy, setViewingStrategy] = useState<Strategy | null>(null);

  useEffect(() => {
    loadStrategies();
  }, []);

  const loadStrategies = async () => {
    setLoading(true);
    try {
      const data = await getStrategies();
      setStrategies(data);
    } catch (error: any) {
      message.error(error.message || t('strategyBuilder.loadFailed', 'Failed to load strategies'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (strategyId: string) => {
    try {
      await deleteStrategy(strategyId);
      message.success(t('strategyBuilder.deleteSuccess', 'Strategy deleted'));
      loadStrategies();
    } catch (error: any) {
      message.error(error.message || t('strategyBuilder.deleteFailed', 'Failed to delete strategy'));
    }
  };

  const getReturnColor = (value: number) => {
    if (value > 0) return theme.candleUp;
    if (value < 0) return theme.candleDown;
    return theme.textSecondary;
  };

  const StrategyCard: React.FC<{ strategy: Strategy }> = ({ strategy }) => {
    const results = strategy.backtest_results;
    
    return (
      <Card
        size="small"
        hoverable
        style={{
          background: theme.surfaceBackground,
          borderColor: theme.surfaceBorder,
          borderRadius: 12
        }}
        styles={{ body: { padding: 16 } }}
      >
        {/* Header */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Text strong style={{ fontSize: 16, color: theme.textPrimary }}>
                {strategy.name}
              </Text>
            </div>
            <Space size={4}>
              <Tag color="blue">{strategy.symbol}</Tag>
              <Tag>{strategy.timeframe}</Tag>
            </Space>
          </div>
          <div style={{ marginTop: 4, color: theme.textSecondary, fontSize: 12 }}>
            {t('strategyBuilder.createdAt', 'Created')}: {new Date(strategy.created_at).toLocaleDateString()}
          </div>
        </div>

        {/* Quick Stats */}
        {results && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 8,
            padding: 12,
            background: theme.toolbarBackground,
            borderRadius: 8,
            marginBottom: 12
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: theme.textSecondary, fontSize: 11 }}>
                {t('strategyBuilder.winRate', 'Win Rate')}
              </div>
              <div style={{ 
                fontSize: 16, 
                fontWeight: 600, 
                color: results.win_rate >= 50 ? theme.candleUp : theme.candleDown 
              }}>
                {results.win_rate.toFixed(1)}%
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: theme.textSecondary, fontSize: 11 }}>
                {t('strategyBuilder.return', 'Return')}
              </div>
              <div style={{ 
                fontSize: 16, 
                fontWeight: 600, 
                color: getReturnColor(results.total_return)
              }}>
                {results.total_return > 0 ? '+' : ''}{results.total_return.toFixed(1)}%
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: theme.textSecondary, fontSize: 11 }}>
                {t('strategyBuilder.trades', 'Trades')}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: theme.textPrimary }}>
                {results.total_trades}
              </div>
            </div>
          </div>
        )}

        {/* Sell Rule Info */}
        <div style={{ 
          padding: 8,
          background: theme.toolbarBackground,
          borderRadius: 6,
          marginBottom: 12,
          fontSize: 12,
          color: theme.textSecondary
        }}>
          <span style={{ fontWeight: 500 }}>{t('strategyBuilder.exitRule', 'Exit')}:</span>{' '}
          {strategy.sell_rule?.type === 'signal' 
            ? t('strategyBuilder.signalBased', 'Signal-based')
            : `TP ${strategy.sell_rule?.take_profit_pct || '-'}% / SL ${strategy.sell_rule?.stop_loss_pct || '-'}%`
          }
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setViewingStrategy(strategy)}
            style={{ flex: 1, borderRadius: 6 }}
          >
            {t('strategyBuilder.viewDetails', 'View')}
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(strategy)}
            style={{ borderRadius: 6 }}
          >
            {t('common.edit', 'Edit')}
          </Button>
          <Popconfirm
            title={t('strategyBuilder.confirmDelete', 'Delete this strategy?')}
            onConfirm={() => handleDelete(strategy.strategy_id)}
            okText={t('common.confirm', 'Yes')}
            cancelText={t('common.cancel', 'No')}
          >
            <Button
              size="small"
              icon={<DeleteOutlined />}
              danger
              style={{ borderRadius: 6 }}
            />
          </Popconfirm>
        </div>
      </Card>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ThunderboltOutlined style={{ fontSize: 24, color: theme.accentPrimary }} />
          <Title level={3} style={{ margin: 0, color: theme.textPrimary }}>
            {t('strategyBuilder.myStrategies', 'My Strategies')}
          </Title>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadStrategies}
            loading={loading}
          >
            {t('common.refresh', 'Refresh')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onCreateNew}
          >
            {t('strategyBuilder.createNew', 'New Strategy')}
          </Button>
        </Space>
      </div>

      {/* Strategy Grid */}
      <Spin spinning={loading}>
        {strategies.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <div style={{ marginBottom: 8 }}>
                  {t('strategyBuilder.noStrategies', 'No strategies yet')}
                </div>
                <Button type="primary" icon={<PlusOutlined />} onClick={onCreateNew}>
                  {t('strategyBuilder.createFirst', 'Create your first strategy')}
                </Button>
              </div>
            }
          />
        ) : (
          <Row gutter={[16, 16]}>
            {strategies.map((strategy) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={strategy.strategy_id}>
                <StrategyCard strategy={strategy} />
              </Col>
            ))}
          </Row>
        )}
      </Spin>

      {/* View Strategy Details Modal */}
      <Modal
        title={viewingStrategy?.name || t('strategyBuilder.strategyDetails', 'Strategy Details')}
        open={!!viewingStrategy}
        onCancel={() => setViewingStrategy(null)}
        footer={[
          <Button key="close" onClick={() => setViewingStrategy(null)}>
            {t('common.close', 'Close')}
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => {
              if (viewingStrategy) {
                onEdit(viewingStrategy);
                setViewingStrategy(null);
              }
            }}
          >
            {t('common.edit', 'Edit')}
          </Button>
        ]}
        width={900}
        styles={{
          content: { background: theme.surfaceBackground }
        }}
      >
        {viewingStrategy?.backtest_results && (
          <BacktestResultsComponent 
            results={viewingStrategy.backtest_results} 
            symbol={viewingStrategy.symbol}
            timeframe={viewingStrategy.timeframe}
          />
        )}
      </Modal>
    </div>
  );
};

export default StrategyList;

